
import rootLogger from '@/utils/root-logger';
import fetcher from '@/utils/fetcher';
import errors from '@/errors';

const logger = rootLogger.create('store/modules/g2w-session/polls');

/* eslint no-param-reassign: ["error", { "props": false }] */

const mutations = {
  setActive(state, payload) {
    state.active = payload.active;
  },

  addAnswered(state, payload) {
    state.answered.push(payload.answered);
  },

  setSharedResults(state, payload) {
    state.sharedResults = payload.sharedResults;
  },

  cachePoll(state, payload) {
    const { questionKey, poll } = payload;
    state.cachedPolls = { ...state.cachedPolls, [questionKey]: poll };
  }
};

const createTestPoll = () => {
  const now = Date.now();

  const answer = 'this is a fake answer ';

  return {
    question: 'why?',
    answers: ['1', '2'].map((item) => `${item} ${answer}`),
    type: Math.random() > 0.5 ? 'MULTI_CHOICE_SINGLE_ANS' : 'MULTI_CHOICE_MULTI_ANS',
    id: now // unique
  };
};

const actions = {
  async cachePolls({commit, dispatch}, payload) {
    const { keys = [] } = payload;
    const promises = [];
    for (const questionKey of keys) {
      const promise = dispatch('retrievePoll', {questionKey})
        .then((poll) => {
          commit('cachePoll', {questionKey, poll});
          return poll;
        }).catch((error) => {
          // this one won't be cached, might work later if&when poll starts, try next key.
          logger.error('failed to retrieve poll for cache', {error, questionKey});
          return {error, questionKey};
        });
      promises.push(promise);
    }
    await Promise.all(promises);
  },

  async retrieve({
    state, dispatch, commit, rootState
  }, payload) {
    if (rootState.testMode) {
      commit('setActive', {active: createTestPoll()});
      return;
    }

    const {questionKey} = payload;
    const cachedPoll = state.cachedPolls[questionKey];
    if (cachedPoll) {
      commit('setActive', {active: cachedPoll});
      return;
    }

    try {
      const poll = await dispatch('retrievePoll', payload);
      commit('cachePoll', {questionKey, poll});
      commit('setActive', {active: poll});
    } catch (error) {
      logger.error('retrievePoll error', {error});
      commit('setError', {error: errors.poll}, { root: true });
    }
  },

  async retrievePoll({rootState, rootGetters}, payload = {}) {
    let url = rootGetters['g2wEnv/pollingServiceBaseUrl'];

    const {eventKey} = rootState.g2wSession.joinInfo;
    url += `/events/${eventKey}/polls`;

    if (payload.questionKey) {
      url += `?questionKey=${payload.questionKey}`;
    }

    try {
      const polls = await fetcher.fetchJSON(url, {}, {operation: 'polls/retrieve'});
      if (Array.isArray(polls) && polls.length === 1) {
        return polls[0];
      }
      logger.error('cannot handle getPollByQuestionKey response', {polls});
      throw new Error('cannot handle getPollByQuestionKey response');
    } catch (error) {
      logger.error('getPollByQuestionKey error', {error});
      throw error;
    }
  },

  async addSubmission({commit, rootState, rootGetters}, payload = {}) {
    logger.log('addSubmission', payload);
    const pollId = payload.poll.id;

    if (rootState.previewMode || rootState.testMode) {
      logger.log('Polling submission disabled in preview mode');
      commit('addAnswered', {answered: pollId});
    } else {
      const {
        eventKey, participantId, sessionKey, sessionType
      } = rootState.g2wSession.joinInfo;

      let url = rootGetters['g2wEnv/pollingServiceBaseUrl'];
      url += `/events/${eventKey}/polls/${pollId}/submissions`;

      const body = {
        attendeeId: rootState.g2wSession.joinInfo.registrantKey,
        participantId,
        sessionKey,
        sessionType: sessionType.toUpperCase(),
        selectedAnswers: payload.selectedAnswers
      };

      const fetchParams = {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify(body)
      };

      await fetcher.fetchText(url, fetchParams, {operation: 'polls/addSubmission'});
      logger.log('addSubmission success');
      commit('addAnswered', {answered: pollId});
    }
  }
};

const state = () => ({
  active: null,
  answered: [],
  sharedResults: null,
  cachedPolls: {}
});

export default {
  namespaced: true,
  state,
  mutations,
  actions
};
